import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type OnChangeFn,
  type RowData,
  type RowSelectionState,
  type SortingState,
  type Table as TanstackTable,
  type VisibilityState,
} from '@tanstack/react-table';
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Rows2,
  Rows3,
  SlidersHorizontal,
} from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { buttonVariants } from './Button';
import { Checkbox } from './checkbox';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { QueryBoundary } from './QueryBoundary';

// Per-column display hints consumed by the DataTable renderer.
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Human label shown in the column-visibility menu (falls back to column id). */
    label?: string;
    /** Extra classes applied to the header cell (`<th>`). */
    headerClassName?: string;
    /** Extra classes applied to each body cell (`<td>`). */
    cellClassName?: string;
    /** Horizontal alignment for header + cell content. */
    align?: 'left' | 'right' | 'center';
  }
}

export type DataTableDensity = 'comfortable' | 'compact';

const DENSITY: Record<DataTableDensity, { head: string; cell: string }> = {
  comfortable: { head: 'px-5 py-3', cell: 'px-5 py-3.5' },
  compact: { head: 'px-5 py-2', cell: 'px-5 py-2' },
};

const SELECT_COLUMN_ID = '__select__';

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  /** Stable row id — important for selection across re-fetches. */
  getRowId?: (row: TData, index: number) => string;
  onRowClick?: (row: TData) => void;
  /** Extra classes applied to each body row (`<tr>`), e.g. to dim inactive rows. */
  rowClassName?: (row: TData) => string | undefined;

  // Async states (rendered via the shared QueryBoundary visuals).
  isLoading?: boolean;
  isError?: boolean;
  emptyState?: React.ReactNode;

  // Sorting — client-side by default; pass `manualSorting` for server-side.
  enableSorting?: boolean;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  manualSorting?: boolean;

  // Row selection (adds a leading checkbox column).
  enableRowSelection?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;

  // Column visibility (adds a "Columns" toolbar control).
  enableColumnVisibility?: boolean;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;

  // Density toggle.
  enableDensityToggle?: boolean;
  density?: DataTableDensity;
  defaultDensity?: DataTableDensity;
  onDensityChange?: (density: DataTableDensity) => void;

  /** Pin the header to the top while scrolling. */
  stickyHeader?: boolean;
  /** Left-aligned toolbar content (e.g. a record count or search field). */
  toolbar?: React.ReactNode;
  /** Footer content rendered below the table (e.g. pagination). */
  footer?: React.ReactNode;
  className?: string;
  tableClassName?: string;
  'aria-label'?: string;
}

/**
 * Enterprise data grid built on TanStack Table v8. Themed to the DentXE design
 * system. Supports sorting, row selection, column visibility and density, plus
 * controlled/uncontrolled usage for server-driven data.
 *
 * @example
 * <DataTable columns={columns} data={rows} enableSorting enableColumnVisibility />
 */
export function DataTable<TData, TValue>({
  columns,
  data,
  getRowId,
  onRowClick,
  rowClassName,
  isLoading,
  isError,
  emptyState,
  enableSorting = true,
  sorting: sortingProp,
  onSortingChange,
  manualSorting = false,
  enableRowSelection = false,
  rowSelection: rowSelectionProp,
  onRowSelectionChange,
  enableColumnVisibility = false,
  columnVisibility: columnVisibilityProp,
  onColumnVisibilityChange,
  enableDensityToggle = false,
  density: densityProp,
  defaultDensity = 'comfortable',
  onDensityChange,
  stickyHeader = false,
  toolbar,
  footer,
  className,
  tableClassName,
  'aria-label': ariaLabel,
}: DataTableProps<TData, TValue>): React.ReactElement {
  const [internalSorting, setInternalSorting] = React.useState<SortingState>([]);
  const [internalVisibility, setInternalVisibility] = React.useState<VisibilityState>({});
  const [internalSelection, setInternalSelection] = React.useState<RowSelectionState>({});
  const [internalDensity, setInternalDensity] = React.useState<DataTableDensity>(defaultDensity);

  const sorting = sortingProp ?? internalSorting;
  const columnVisibility = columnVisibilityProp ?? internalVisibility;
  const rowSelection = rowSelectionProp ?? internalSelection;
  const density = densityProp ?? internalDensity;

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    if (sortingProp === undefined) setInternalSorting(next);
    onSortingChange?.(next);
  };
  const handleVisibilityChange: OnChangeFn<VisibilityState> = (updater) => {
    const next = typeof updater === 'function' ? updater(columnVisibility) : updater;
    if (columnVisibilityProp === undefined) setInternalVisibility(next);
    onColumnVisibilityChange?.(next);
  };
  const handleSelectionChange: OnChangeFn<RowSelectionState> = (updater) => {
    const next = typeof updater === 'function' ? updater(rowSelection) : updater;
    if (rowSelectionProp === undefined) setInternalSelection(next);
    onRowSelectionChange?.(next);
  };
  const setDensity = (next: DataTableDensity) => {
    if (densityProp === undefined) setInternalDensity(next);
    onDensityChange?.(next);
  };

  const resolvedColumns = React.useMemo<ColumnDef<TData, TValue>[]>(() => {
    if (!enableRowSelection) return columns;
    const selectionColumn: ColumnDef<TData, TValue> = {
      id: SELECT_COLUMN_ID,
      enableSorting: false,
      enableHiding: false,
      meta: { headerClassName: 'w-10', cellClassName: 'w-10' },
      header: ({ table }) => (
        <Checkbox
          aria-label="Select all rows"
          checked={
            table.getIsAllPageRowsSelected()
              ? true
              : table.getIsSomePageRowsSelected()
                ? 'indeterminate'
                : false
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          onClick={(event) => event.stopPropagation()}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          aria-label="Select row"
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          onClick={(event) => event.stopPropagation()}
        />
      ),
    };
    return [selectionColumn, ...columns];
  }, [columns, enableRowSelection]);

  const table = useReactTable({
    data,
    columns: resolvedColumns,
    state: { sorting, columnVisibility, rowSelection },
    getRowId,
    enableSorting,
    enableRowSelection,
    manualSorting,
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: handleVisibilityChange,
    onRowSelectionChange: handleSelectionChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
  });

  const cellPadding = DENSITY[density].cell;
  const headPadding = DENSITY[density].head;
  const selectedCount = table.getSelectedRowModel().rows.length;
  const showToolbar = Boolean(toolbar) || enableColumnVisibility || enableDensityToggle;

  return (
    <div className={className}>
      {showToolbar && (
        <div className="flex items-center justify-between gap-3 border-b border-[var(--border-subtle)] px-5 py-3.5">
          <div className="flex min-w-0 items-center gap-3">
            {toolbar}
            {enableRowSelection && selectedCount > 0 && (
              <span className="text-[12px] font-medium text-ink-500">{selectedCount} selected</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {enableDensityToggle && <DensityToggle density={density} onChange={setDensity} />}
            {enableColumnVisibility && <ColumnVisibilityMenu table={table} />}
          </div>
        </div>
      )}

      <QueryBoundary
        isPending={Boolean(isLoading)}
        isError={Boolean(isError)}
        data={data}
        isEmpty={data.length === 0}
        empty={emptyState}
      >
        {() => (
          <>
            <div className="overflow-x-auto">
              <table className={cn('w-full text-sm', tableClassName)} aria-label={ariaLabel}>
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr
                      key={headerGroup.id}
                      className={cn(
                        'text-left text-[11px] uppercase tracking-wider text-ink-500 bg-surface-50',
                        stickyHeader && 'sticky top-0 z-10',
                      )}
                    >
                      {headerGroup.headers.map((header) => {
                        const align = header.column.columnDef.meta?.align ?? 'left';
                        const canSort = header.column.getCanSort();
                        const sortDir = header.column.getIsSorted();
                        return (
                          <th
                            key={header.id}
                            scope="col"
                            className={cn(
                              'font-semibold',
                              headPadding,
                              align === 'right' && 'text-right',
                              align === 'center' && 'text-center',
                              header.column.columnDef.meta?.headerClassName,
                            )}
                          >
                            {header.isPlaceholder ? null : canSort ? (
                              <button
                                type="button"
                                onClick={header.column.getToggleSortingHandler()}
                                className={cn(
                                  'inline-flex select-none items-center gap-1 uppercase tracking-wider transition-colors hover:text-ink-700',
                                  align === 'right' && 'flex-row-reverse',
                                )}
                                aria-label={`Sort by ${header.column.columnDef.meta?.label ?? header.column.id}`}
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {sortDir === 'asc' ? (
                                  <ChevronUp className="size-3.5" />
                                ) : sortDir === 'desc' ? (
                                  <ChevronDown className="size-3.5" />
                                ) : (
                                  <ChevronsUpDown className="size-3.5 opacity-40" />
                                )}
                              </button>
                            ) : (
                              flexRender(header.column.columnDef.header, header.getContext())
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-[var(--border-subtle)]">
                  {table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      data-state={row.getIsSelected() ? 'selected' : undefined}
                      className={cn(
                        'transition-colors',
                        onRowClick && 'cursor-pointer',
                        row.getIsSelected() ? 'bg-brand-50' : 'hover:bg-surface-50',
                        rowClassName?.(row.original),
                      )}
                      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const align = cell.column.columnDef.meta?.align ?? 'left';
                        return (
                          <td
                            key={cell.id}
                            className={cn(
                              cellPadding,
                              align === 'right' && 'text-right',
                              align === 'center' && 'text-center',
                              cell.column.columnDef.meta?.cellClassName,
                            )}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {footer}
          </>
        )}
      </QueryBoundary>
    </div>
  );
}

function DensityToggle({
  density,
  onChange,
}: {
  density: DataTableDensity;
  onChange: (density: DataTableDensity) => void;
}): React.ReactElement {
  return (
    <div
      role="group"
      aria-label="Row density"
      className="flex items-center gap-0.5 rounded-lg border border-[var(--border-subtle)] p-0.5"
    >
      <button
        type="button"
        aria-label="Comfortable density"
        aria-pressed={density === 'comfortable'}
        onClick={() => onChange('comfortable')}
        className={cn(
          'flex size-7 items-center justify-center rounded-md text-ink-500 transition-colors hover:text-ink-700',
          density === 'comfortable' && 'bg-surface-100 text-ink-900',
        )}
      >
        <Rows2 className="size-4" />
      </button>
      <button
        type="button"
        aria-label="Compact density"
        aria-pressed={density === 'compact'}
        onClick={() => onChange('compact')}
        className={cn(
          'flex size-7 items-center justify-center rounded-md text-ink-500 transition-colors hover:text-ink-700',
          density === 'compact' && 'bg-surface-100 text-ink-900',
        )}
      >
        <Rows3 className="size-4" />
      </button>
    </div>
  );
}

function ColumnVisibilityMenu<TData>({
  table,
}: {
  table: TanstackTable<TData>;
}): React.ReactElement {
  const hideableColumns = table.getAllLeafColumns().filter((column) => column.getCanHide());
  return (
    <Popover>
      <PopoverTrigger
        className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-2 text-ink-700')}
      >
        <SlidersHorizontal className="size-4" />
        Columns
      </PopoverTrigger>
      <PopoverContent align="end" className="w-52 p-2">
        <div className="px-2 py-1.5 text-[11px] uppercase tracking-wider text-ink-500">
          Toggle columns
        </div>
        {hideableColumns.map((column) => (
          <label
            key={column.id}
            className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-ink-700 hover:bg-surface-50"
          >
            <Checkbox
              checked={column.getIsVisible()}
              onCheckedChange={(value) => column.toggleVisibility(!!value)}
            />
            <span className="capitalize">{column.columnDef.meta?.label ?? column.id}</span>
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}
